/* eslint-disable react-hooks/refs -- Refs updated during render for stable selector/isEqual references across re-renders */
import { RENDER_DEBUG } from "../lib/debug-flags.js";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useZero } from "@rocicorp/zero/react";
import { acquireView } from "../lib/zero-view-cache.js";

/**
 * Selector-based subscription to Zero data. Like Redux's useSelector but
 * for Zero queries — components only re-render when their specific slice
 * of data changes.
 *
 * Uses zero.materialize() + useSyncExternalStore instead of useQuery.
 * No deep clone on the hot path — the selector runs on view.data (the
 * live IVM data) at listener-fire time, and only notifies React when
 * the selector output changes.
 *
 * For collection queries only (T[]). Singular queries (.one()) should
 * stay on useQuery — their deep-clone cost is negligible.
 */

type ResultStatus = {
  type: "unknown" | "complete" | "error";
  retry?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
};

const UNKNOWN_STATUS: ResultStatus = { type: "unknown" };

/**
 * Subscribe to a derived slice of Zero query data. The selector runs on
 * every data change, but React only re-renders when the output changes.
 *
 * @param cacheKey - Unique key for view sharing (e.g., "users.all")
 * @param query - The Zero query, or null to disable
 * @param selector - Pure function: (data: T[]) => Selected
 * @param options.isEqual - Custom equality check (default: Object.is)
 * @param options.ttl - Zero TTL string (default: "5m")
 */
export function useZeroSelector<Selected>(
  cacheKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selector: (data: any[]) => Selected,
  options?: {
    isEqual?: (a: Selected, b: Selected) => boolean;
    ttl?: string;
    /** Default value returned before the view delivers first data.
     *  MUST be a stable reference (e.g., module-level constant). */
    defaultValue?: Selected;
  },
): [Selected, ResultStatus] {
  const zero = useZero();
  const isEqual = options?.isEqual ?? Object.is;
  const ttl = options?.ttl;
  const defaultValue = options?.defaultValue;

  // Lazy-init the state ref. Eager `useRef({...})` allocates a new object
  // every render and discards all but the first — fine in vanilla React,
  // but the React Compiler can hoist the literal in ways that don't
  // guarantee the ref's identity contract. Lazy-init is the canonical
  // pattern and unambiguously stable.
  type State = {
    selected: Selected;
    status: ResultStatus;
    version: number;
    initialized: boolean;
  };
  const stateRef = useRef<State | null>(null);
  if (stateRef.current === null) {
    stateRef.current = {
      selected: (defaultValue ?? undefined) as Selected,
      status: UNKNOWN_STATUS,
      version: 0,
      initialized: false,
    };
  }

  // Store in refs so the effect doesn't re-run on identity changes
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;
  const queryRef = useRef(query);
  queryRef.current = query;

  // Subscribers for useSyncExternalStore — lazy-init for the same reason
  // as stateRef above.
  const listenersRef = useRef<Set<() => void> | null>(null);
  if (listenersRef.current === null) {
    listenersRef.current = new Set();
  }

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current?.add(listener);
    return () => {
      listenersRef.current?.delete(listener);
    };
  }, []);

  const getSnapshot = useCallback((): State => {
    if (stateRef.current === null) {
      stateRef.current = {
        selected: undefined as unknown as Selected,
        status: UNKNOWN_STATUS,
        version: 0,
        initialized: false,
      };
    }
    return stateRef.current;
  }, []);

  // Track whether query is null for the effect
  const queryIsNull = query === null;

  // View lifecycle: acquire on mount/cacheKey change, release on cleanup
  useEffect(() => {
    if (queryIsNull) {
      // Null query — reset to uninitialized state.
      // Do NOT call the selector with [] — for module-level singletons,
      // that would reset their prevMap to empty.
      const cur = stateRef.current;
      if (cur && cur.initialized) {
        stateRef.current = {
          selected: undefined as unknown as Selected,
          status: UNKNOWN_STATUS,
          version: cur.version + 1,
          initialized: false,
        };
        const ls = listenersRef.current;
        if (ls) for (const l of ls) l();
      }
      return;
    }

    const { view, release } = acquireView(
      zero,
      cacheKey,
      queryRef.current,
      ttl,
    );

    const unsubscribe = view.addListener(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (data: any, resultType: string, error?: any) => {
        const sel = selectorRef.current;
        const eq = isEqualRef.current;
        const prev = stateRef.current;
        if (!prev) return;
        const statusChanged = prev.status.type !== resultType;

        // If data is undefined (no results yet), skip selector call to
        // avoid poisoning module-level singleton selectors with empty input.
        if (data === undefined) {
          if (!prev.initialized || statusChanged) {
            stateRef.current = {
              selected: prev.selected,
              status:
                resultType === "error"
                  ? { type: "error" as const, error }
                  : { type: resultType as "unknown" | "complete" },
              version: prev.version + 1,
              initialized: prev.initialized,
            };
            const ls = listenersRef.current;
            if (ls) for (const l of ls) l();
          }
          return;
        }
        const nextSelected = sel(data);
        const selectedChanged = !eq(prev.selected, nextSelected);

        if (!statusChanged && !selectedChanged) {
          if (RENDER_DEBUG)
            console.debug(
              `[useZeroSelector:${cacheKey}] STABLE — selector output unchanged`,
            );
          return;
        }

        const nextStatus: ResultStatus =
          resultType === "error"
            ? { type: "error", error }
            : { type: resultType as "unknown" | "complete" };

        // D.2: Status-only flips (unknown → complete) do NOT trigger a
        // React re-render. Every consumer in the codebase destructures
        // `const [x] = hook()` and ignores the status tuple — audited at
        // the time of this change. Suppressing these re-renders closes
        // the cold-startup "render storm" where every Zero query
        // transitioning from unknown → complete caused a parent
        // re-render cascade. stateRef.status is still updated so the
        // next selected-change emission reports the current status.
        if (!selectedChanged && statusChanged) {
          stateRef.current = {
            selected: prev.selected,
            status: nextStatus,
            version: prev.version + 1,
            initialized: true,
          };
          if (RENDER_DEBUG)
            console.debug(
              `[useZeroSelector:${cacheKey}] STATUS-ONLY — suppressed`,
            );
          return;
        }

        if (RENDER_DEBUG)
          console.debug(`[useZeroSelector:${cacheKey}] CHANGED`, {
            selectedChanged,
            statusChanged,
          });

        stateRef.current = {
          selected: selectedChanged ? nextSelected : prev.selected,
          status: statusChanged ? nextStatus : prev.status,
          version: prev.version + 1,
          initialized: true,
        };

        // Notify React
        const ls = listenersRef.current;
        if (ls) for (const l of ls) l();
      },
    );

    return () => {
      unsubscribe();
      release();
    };
    // cacheKey changes when the query identity should change (e.g., different channelId).
    // queryIsNull tracks null/non-null transitions. query object identity is in queryRef.
  }, [zero, cacheKey, queryIsNull, ttl]);

  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [state.selected, state.status];
}
